import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../context/BreadcrumbContext";
import { AdminCapabilityProvider, AdminCapabilityContextValue, hasCapability } from "../../context/AdminCapabilityContext";
import AdminSubNav from "../../components/admin/shared/AdminSubNav";
import { fetchAdminCapabilities } from "../../api/admin";
import { widePageContainer, card, pageTitle } from "../../styles";

interface Props {
  user: User;
}

// ─── Top-level admin sections ────────────────────────────────────────────────
// One entry per §2 top-level route. `capability` is what a future scoped-delegate
// role would need to see this section — null means "any admin, no capability
// gate" (true for every entry today; Metrics/Ops Home have no dedicated capability
// at all). `end` matches AdminSubNav/NavLink semantics — Overview sits at the bare
// /admin path, a prefix of every other section, so it needs exact matching.
const SECTIONS: { to: string; labelKey: string; capability: string | null; end?: boolean }[] = [
  { to: "/admin",              labelKey: "admin.tabs.opsHome",      capability: null, end: true },
  { to: "/admin/competitions", labelKey: "admin.tabs.competitions", capability: "competition:configure" },
  { to: "/admin/clubs",        labelKey: "admin.tabs.clubs",        capability: "club:manage" },
  { to: "/admin/users",        labelKey: "admin.tabs.users",        capability: "user:moderate" },
  { to: "/admin/leagues",      labelKey: "admin.tabs.leagues",      capability: "league:moderate" },
  { to: "/admin/metrics",      labelKey: "admin.tabs.metrics",      capability: null },
];

const DEV_SECTION = { to: "/admin/dev", labelKey: "admin.tabs.dev", capability: null, end: false };

export default function AdminLayout({ user }: Props) {
  const { t } = useTranslation();
  useBreadcrumbs([{ label: t("common.home"), to: "/overview" }, { label: t("admin.title") }]);

  const [capabilityState, setCapabilityState] = useState<AdminCapabilityContextValue>({
    capabilities: [], competitionIds: [], loading: true, error: false,
  });

  useEffect(() => {
    let cancelled = false;
    fetchAdminCapabilities(user)
      .then(({ capabilities, competitionIds }) => {
        if (!cancelled) setCapabilityState({ capabilities, competitionIds, loading: false, error: false });
      })
      .catch(() => {
        if (!cancelled) setCapabilityState((prev) => ({ ...prev, loading: false, error: true }));
      });
    return () => { cancelled = true; };
  }, [user]);

  const isLocalhost = process.env.REACT_APP_API_URL?.includes("localhost");
  const allSections = isLocalhost ? [...SECTIONS, DEV_SECTION] : SECTIONS;
  const visibleSections = allSections.filter((s) => hasCapability(capabilityState, s.capability));

  return (
    <AdminCapabilityProvider value={capabilityState}>
      <div style={{ ...widePageContainer, padding: "1.5rem 0" }}>
        <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: "1.25rem" }}>
          <div style={{ padding: "1rem 1.5rem", borderBottom: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: "1rem" }}>
            <h1 style={pageTitle}>{t("admin.title")}</h1>
          </div>
          <AdminSubNav items={visibleSections.map((s) => ({ to: s.to, label: t(s.labelKey), end: !!s.end }))} />
        </div>

        <Outlet />
      </div>
    </AdminCapabilityProvider>
  );
}
